import jwtDecode from "jwt-decode";
import * as React from "react";
import { useHistory, useLocation } from "react-router";

import {
  AuthenticateCommand,
  Configuration,
  EPlatform,
  EUserProfile,
  IAuthenticateCommand,
  UserDto,
  UserProfileActionDto,
  UsersApi,
} from "../api";
import { EDrupalRoutes } from "../drupal/EDrupalRoutes";
import { ERoutes } from "../ERoutes";
import { getbasicconfig, useLocalStorage, useTl } from "../hooks";
import { usePlatformContext } from "./PlatformContext";
import { getBaseUrl } from "../baseUrl";

interface IAuthProviderProps extends React.PropsWithChildren {}

export interface ErrorDetails {
  statusCode?: number;
  message?: string | null;
  errorCode?: any;
}

interface IAuthContext {
  login: (auth: IAuthenticateCommand) => Promise<UserDto>;
  logout: (reason: logoutReasons) => void;
  isLoading: boolean;
  user?: UserDto;
  logoutReason?: logoutReasons;
  loginError?: ErrorDetails;
  matchModule: (module: string) => boolean;
  matchTab: (tab: string) => boolean;
  userPermissions: UserProfileActionDto[];
  getCurrentPermission: () => UserProfileActionDto;
  canBlockRequest: () => boolean;
  hasAction: (module: string) => boolean;
}

export type logoutReasons = "FORBIDDEN" | "UNAUTHORIZED" | "SESSIONEXPIRED" | "NORMAL";
const AuthContext = React.createContext<IAuthContext>(null);

const getLocaleStorageKey = (env: EPlatform) => {
  if (env === EPlatform.CARE4DOGS) {
    return "user_c4d";
  }
  if (env === EPlatform.PORTAL) {
    return "user";
  }
  return "user";
};

const AuthProvider: React.FunctionComponent<IAuthProviderProps> = ({ children }) => {
  const { platform } = usePlatformContext();

  const LOCALSTORAGE_AUTH_KEY = React.useMemo(() => getLocaleStorageKey(platform), [platform]);

  // Load from localstorage
  const { tUnsafe } = useTl();
  const history = useHistory();
  const userApi = React.useMemo(
    () =>
      new UsersApi({
        basePath: getBaseUrl(),
      }),
    []
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const [logoutReason, setLogoutReason] = React.useState<logoutReasons>();
  const [storedUser, setStoredUser] = useLocalStorage<UserDto>(LOCALSTORAGE_AUTH_KEY, null);

  const [user, setUser] = React.useState(storedUser);
  const [loginError, setCurrentError] = React.useState<ErrorDetails>();
  const location = useLocation();

  const [userPermissions, setUserPermissions] = React.useState<UserProfileActionDto[]>(null);

  const login = async (auth: AuthenticateCommand) => {
    try {
      setIsLoading(true);
      const userFromBackend = await userApi.authenticate(auth);
      if (userFromBackend && userFromBackend.mustResetPassword) {
        setStoredUser(userFromBackend);
        setUser(userFromBackend);
        setIsLoading(false);
        history.push(`${ERoutes.changePassword}`);
        return;
      }
      if (userFromBackend && userFromBackend.mustChangePassword) {
        setStoredUser(userFromBackend);
        setUser(userFromBackend);
        setIsLoading(false);
        history.push(`${EDrupalRoutes.changePassword}`);
        return userFromBackend;
      }
      if (userFromBackend) {
        setStoredUser(userFromBackend);
      }
      setUser(userFromBackend);
      setCurrentError(null);
      setIsLoading(false);
      if (userFromBackend.platform === EPlatform.CARE4DOGS && userFromBackend.defaultRoute) {
        history.push(userFromBackend.defaultRoute);
      }
      return userFromBackend;
    } catch (error) {
      const details: ErrorDetails = JSON.parse(error.response);
      setCurrentError({
        ...details,
        message: tUnsafe(`BackendError_${details.errorCode}`),
      });
    }
    setIsLoading(false);
  };

  const logout = React.useCallback(
    (reason: logoutReasons = "NORMAL") => {
      setwasForbidden(reason === "FORBIDDEN");
      setUser(null);
      setLogoutReason(reason);
      setUserPermissions(null);
      localStorage.removeItem(LOCALSTORAGE_AUTH_KEY);
    },
    [LOCALSTORAGE_AUTH_KEY]
  );

  React.useEffect(() => {
    if (userPermissions === null && storedUser) {
      const config = getbasicconfig({
        token: storedUser.token,
        logout: logout,
      });
      const userApiWithToken = new UsersApi(config);
      userApiWithToken
        .getUserProfileActions()
        .then(setUserPermissions)
        .catch((error) => {
          console.error(error);
        });
    }
  }, [logout, storedUser, userApi, userPermissions]);

  const [wasForbidden, setwasForbidden] = React.useState(false);

  const expirationDate = React.useMemo(() => {
    if (user) {
      const decoded: any = jwtDecode(user?.token);
      return new Date(decoded.exp * 1000);
    } else {
      return null;
    }
  }, [user]);

  const refreshDateThreshold = React.useMemo(() => {
    if (!expirationDate) {
      return 0;
    }
    const now = new Date();
    const diff = expirationDate.getTime() - now.getTime();

    return new Date(now.getTime() + diff * 0.75);
  }, [expirationDate]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      if (refreshDateThreshold && expirationDate) {
        const now = new Date();
        if (now > expirationDate) {
          logout("SESSIONEXPIRED");
        }
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [expirationDate, logout, refreshDateThreshold]);

  React.useEffect(() => {
    if (userPermissions && userPermissions.length === 0 && storedUser) {
      if (storedUser.platform === EPlatform.CARE4DOGS) {
        history.push(`${ERoutes.unauthorized}`);
      }
    }
  }, [history, storedUser, userPermissions]);

  const matchModule = React.useCallback(
    (module: string) => {
      if (user?.profile === EUserProfile.God) return true;
      if (!userPermissions) return false;
      return userPermissions.some((p) => p.module.toUpperCase() === module.toUpperCase());
    },
    [user, userPermissions]
  );
  const matchTab = React.useCallback(
    (tab: string) => {
      if (!userPermissions) return false;
      const module = location.pathname.split("/")[1].toUpperCase();
      return userPermissions.some(
        (p) => p.tab && p.module.toUpperCase() === module && p.tab.toUpperCase() === tab.toUpperCase()
      );
    },
    [location.pathname, userPermissions]
  );

  const canBlockRequest = React.useCallback(() => {
    return userPermissions.some(
      (p) =>
        p.module.toLowerCase() === "requests" && p.permission.toUpperCase() === "E" && p.tab.toLowerCase() === "block"
    );
  }, [userPermissions]);

  const getCurrentPermission = React.useCallback(() => {
    let permission = null;
    if (!userPermissions) return null;
    const module = location.pathname.split("/")[1].toUpperCase();
    permission = userPermissions.find(
      (p) => p.module.toUpperCase() === module.toUpperCase() && (p.tab === "SEARCH" || p.tab === "" || !p.tab)
    );
    if (location.pathname.split("/").length > 4) {
      const tab = location.pathname.split("/")[3].toUpperCase();
      permission = userPermissions.find(
        (p) => p.tab && p.module.toUpperCase() === module && p.tab.toUpperCase() === tab.toUpperCase()
      );
    }
    return permission;
  }, [location.pathname, userPermissions]);

  const hasAction = React.useCallback(
    (module: string) => {
      return !!userPermissions.find((c) => c.module.toUpperCase() === module.toUpperCase());
    },
    [userPermissions]
  );

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        user,
        isLoading,
        logoutReason,
        loginError,
        matchModule,
        matchTab,
        userPermissions,
        getCurrentPermission,
        canBlockRequest,
        hasAction,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => React.useContext(AuthContext);

export { AuthProvider, useAuth };
